import axios from "axios";
import { QCCATSessionStorage } from "./qccatStorage";
import * as uuid from "uuid";
import * as moment from "moment";

import { IAjaxMonitorConfig } from "./../models";
export interface INameValue { name: string; value: string; }
export interface IConfig { apiUrl: string; loginUrl: string; notifyRequestRejectedUrl?: string; }
export const unauthorizedErrorMessage = "Unauthorized";
export const defaultErrorMessage = "An error occurred making the request.";

/**
 * axios interceptors for ajax monitoring begin
 */
export interface IJsMonitorHeader{
	start?:	string;
	end?: 	string;
}

export interface IAjaxMonitorLog{
    id?: string;
	jsStartUTC?: string;
	jsFinishUTC?:string;
	serverStartUTC?: string;
	serverFinishUTC?:string;	
	request?: any;
	response?: string;
	url?: string;
}

const DateFormat = "YYYYMMDDHHmmssSSS";
const insertAjaxTimeEndpointName = "InsertAjaxTime";
const insertAjaxTimeEndpoint = "api/JsMonitor/InsertAjaxTime";
const jsMonitorHeaderRequest = "x-jsMonitor-request";
const jsMonitorHeaderResponse = "x-jsmonitor-response";

// Add a request interceptor
axios.interceptors.request.use((config) => {

	//Inject js start time to the header of request.
	if (ApiService.ajaxMonitorConfig  && ApiService.ajaxMonitorConfig.enabled)
	{	
		config.headers[jsMonitorHeaderRequest] = JSON.stringify(
			{
				start: moment(new Date()).utc().format(DateFormat)
			});
	}
	//process the original request.
	return config;	
  }, (error) => {
    return Promise.reject(error);
});

// Add a response interceptor
axios.interceptors.response.use((response) =>{

	if (ApiService.ajaxMonitorConfig  && ApiService.ajaxMonitorConfig.enabled){		
		//Skip the request of insertAjaxTimeToServer itself
		if (response.config.url.toString().includes(insertAjaxTimeEndpointName)) return response;	
		//Skip when x-jsmonitor header is missing.
		if(  !response.config.headers[jsMonitorHeaderRequest] || !response.headers[jsMonitorHeaderResponse] ){
			return response;			
		}

		const currentTimeUTC = moment(new Date()).utc().format(DateFormat);
		const lastPushUpTime = QCCATSessionStorage.getLastAjaxMonitorPushUpTime() || moment(new Date().setFullYear(1900)).utc().format(DateFormat)
		const pushUpTimespanSeconds = Math.abs(moment(currentTimeUTC, DateFormat).diff(moment(lastPushUpTime, DateFormat),'seconds'));
		//Skip within PushUpMaxSeconds
		if (pushUpTimespanSeconds < ApiService.ajaxMonitorConfig.ajaxPushUpMaxSeconds) return response;

		//Deserialize jsMonitor from headers
		let jsMonitorResponse : IJsMonitorHeader;
		let jsMonitorRequest : IJsMonitorHeader;		
		try {
			jsMonitorResponse  = JSON.parse(response.headers[jsMonitorHeaderResponse]);		
			jsMonitorRequest  = JSON.parse((response as any).config.headers[jsMonitorHeaderRequest]);			
		} catch (e) {				
			//Skip when can not parse pushed objects
			return response;
		}

		//Collect all times
		const startTimeUTCServer = jsMonitorResponse.start;
		const endTimeUTCServer = jsMonitorResponse.end;
		const startTimeUTCJS = jsMonitorRequest.start;
		const endTimeUTCJS = currentTimeUTC;		
		let jSTimespanMilliseconds = Math.abs(moment(endTimeUTCJS, DateFormat).diff(moment(startTimeUTCJS, DateFormat)));

		//When JS timespan more than configured value, push up to server
		if (jSTimespanMilliseconds > ApiService.ajaxMonitorConfig.ajaxMilliSeconds){
			let ajaxMonitorLog :  IAjaxMonitorLog = {
				id: uuid.v4(),
				url: response.config.url,
				jsStartUTC: moment(startTimeUTCJS, DateFormat).toISOString(),
				jsFinishUTC: moment(endTimeUTCJS, DateFormat).toISOString(),
				serverStartUTC: moment(startTimeUTCServer, DateFormat).toISOString(),
				serverFinishUTC: moment(endTimeUTCServer, DateFormat).toISOString(),
				request: {body: JSON.stringify(response.config.data)},
				response: JSON.stringify(response.data),
			}

			insertAjaxTimeToServer(ajaxMonitorLog);
		}
	}

	//process the original response.
	return response;		
  },  (error) => {
    return Promise.reject(error);
});

const insertAjaxTimeToServer = (res : IAjaxMonitorLog) => {
	const apiService = new ApiService({apiUrl: ApiService.ajaxMonitorServer, loginUrl: ""});
	QCCATSessionStorage.setLastAjaxMonitorPushUpTime(moment(new Date()).utc().format(DateFormat));

	return new Promise((resolve, reject) => {
		apiService.post(insertAjaxTimeEndpoint, res).then((response) => {
			console.log("push up Ajax monitor obj to server.");
			resolve(response);
		})
		.catch((error) => {
			console.log("push up Ajax monitor error, ", error);
		}
		);	  
	});
}

/**
 * axios interceptors for ajax monitoring end
 */  


export class ApiService {
	private _config: IConfig;
	private _alternateToken;

	constructor(config: IConfig) {
		this._config = config;
	}

	public static ajaxMonitorConfig;
	public static ajaxMonitorServer;

	public post(apiRoute: string, data: any = {}, additionalHeaders: any = null) {
		let service = this;
		let axiosConfig = this.getDefaultConfigWithToken();

		if (additionalHeaders) {
			Object.keys(additionalHeaders).map(k => {
				axiosConfig.headers[k] = additionalHeaders[k];
			});			
		}

		let apiUrl = this._config.apiUrl;
		
		return new Promise((resolve, reject) => {
			axios.post(this.getFullUrl(apiUrl, apiRoute), data, axiosConfig)
				.then(function (response) {						
					service.handleRequestRejectedResponse(response, reject, service.getFullUrl(apiUrl, apiRoute), axiosConfig);
					QCCATSessionStorage.resetApiRetries();
					resolve(response.data);
				})
				.catch(function (error) {
					console.log("ApiService.Post Error", error);
					let isAuthorizationError = service.isUnauthorized(error);
					service.createRejectResultFromError(reject, error, isAuthorizationError);
				});
		});
	}

	public get(apiRoute: string, params: INameValue[] = []) {
		let service = this;
		let urlParams = this.getUrlParams(params);
		let url = this.getFullUrl(this._config.apiUrl, apiRoute) + urlParams;
		let axiosConfig = this.getDefaultConfigWithToken();

		return new Promise((resolve, reject) => {
			axios.get(url, axiosConfig)
				.then(function (response) {
					service.handleRequestRejectedResponse(response, reject, url, axiosConfig);
					QCCATSessionStorage.resetApiRetries();
					resolve(response.data);
				})
				.catch(function (error) {
					console.log("ApiService.Get Error", error);
					let isAuthorizationError = service.isUnauthorized(error);
					service.createRejectResultFromError(reject, error, isAuthorizationError);
				});
		});
	}

	private handleRequestRejectedResponse(response, reject, requestUrl, axiosConfig) {
		if (this.isRequestRejectedResponse(response))
		{			
			this.notifyOfRequestRejectedResponse(requestUrl, axiosConfig)
			reject({ message: "Invalid request" });
		}
	}

	private isRequestRejectedResponse(response) {
		if (!response.status || !response.data)
			return false;
		
		const responseData = response.data.toString();

		if (response.status === 200 && responseData && responseData.startsWith("<html><head><title>Request Rejected</title></head>"))
		{			
			return true;
		}

		return false;
	}

	private notifyOfRequestRejectedResponse(initialRequestUrl, axiosConfig) {
		if (this._config.notifyRequestRejectedUrl) {		
			axios.post(
				this.getFullUrl(this._config.apiUrl, this._config.notifyRequestRejectedUrl), //"api/RequestIssue/NotifyRequestRejectedResponse" 
				{
					url: initialRequestUrl
				}, 
				axiosConfig
			);
		}
	}	

	private createRejectResultFromError(reject, error, isAuthorizationError) {
		if (error.response && error.response.data) {
			let responseData = error.response.data;
			reject({
				message: responseData.message,
				fields: responseData.fields
			});
		}
		else {
			reject({
				message: isAuthorizationError ? unauthorizedErrorMessage : defaultErrorMessage,
				fields: {}
			});
		}
	}

	private getFullUrl(apiUrl, apiRoute) {
		return apiUrl + apiRoute;
	}

	private getUrlParams(params: INameValue[]): string {
		let urlParams = "";
		params.forEach(element => {
			if (urlParams !== "") {
				urlParams += "&";
			}
			else {
				urlParams += "?";
			}

			urlParams += element.name + "=" + element.value;
		});
		return urlParams;
	}

	private getDefaultConfigWithToken() {
		return  {
			headers: { "Authorization": "Bearer " + this.getToken() }
		};
	}

	private getToken() {
		let user = QCCATSessionStorage.getUser();
		return (user && user.userAccess && user.userAccess.access_token)
			? user.userAccess.access_token
			: "";
	}

	private isUnauthorized(error: any) {
		let isAuthorizationError = false;
		if (error.response && error.response.status) {
			if (error.response.status === 401) {
				this.redirectToLoginUrl();
			}
			else if (error.response.status === 403) {
				let token = this.getToken();
				if (token === "") {
					this.redirectToLoginUrl();
				}
				else {
					isAuthorizationError = true;
					QCCATSessionStorage.incrementApiRetries();

					if (QCCATSessionStorage.reachedMaxApiRetries()) {
						this.redirectToLoginUrl();
					}
					else {
						window.history.back();
					}
				}
			}
		}
		return isAuthorizationError;
	}

	private redirectToLoginUrl = () => {
		QCCATSessionStorage.removeUser();
		QCCATSessionStorage.resetApiRetries();
		window.location.href = this._config.loginUrl;
	}
}
